import React, { useEffect } from 'react';
import { Modal, Button, Space, message, Select, Spin } from 'antd';
import { PrinterOutlined, EyeOutlined } from '@ant-design/icons';
import {
  usePrintReceiptMutation,
  usePreviewReceiptMutation,
  useGetPrinterConfigurationsQuery,
} from '../../services/printersApi';

interface ReceiptPreviewModalProps {
  visible: boolean;
  onCancel: () => void;
  receiptData: any; // ReceiptDto from backend
  onPrintSuccess?: () => void;
}

const ReceiptPreviewModal: React.FC<ReceiptPreviewModalProps> = ({
  visible,
  onCancel,
  receiptData,
  onPrintSuccess,
}) => {
  const [messageApi, contextHolder] = message.useMessage();
  const [selectedPrinter, setSelectedPrinter] = React.useState<string | undefined>();
  const [previewText, setPreviewText] = React.useState<string>('');

  const { data: printers, isLoading: loadingPrinters } = useGetPrinterConfigurationsQuery();
  const [printReceipt, { isLoading: printing }] = usePrintReceiptMutation();
  const [previewReceipt, { isLoading: previewing }] = usePreviewReceiptMutation();

  // Auto-select default printer
  useEffect(() => {
    if (printers && !selectedPrinter) {
      const defaultPrinter = printers.find((p) => p.isDefault);
      if (defaultPrinter) {
        setSelectedPrinter(defaultPrinter.id);
      }
    }
  }, [printers, selectedPrinter]);

  // Auto-generate preview when printer changes
  useEffect(() => {
    if (visible && receiptData && selectedPrinter) {
      handlePreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, selectedPrinter]);

  const handlePreview = async () => {
    try {
      const result = await previewReceipt({
        receiptData,
        printerConfigId: selectedPrinter,
      }).unwrap();
      setPreviewText(result.formattedReceipt);
    } catch (error: any) {
      messageApi.error(error?.data?.message || 'Error al generar vista previa');
      console.error('Preview error:', error);
    }
  };

  const handlePrint = async () => {
    try {
      const result = await printReceipt({
        receiptData,
        printerConfigId: selectedPrinter,
      }).unwrap();

      if (result.status === 'COMPLETED') {
        messageApi.success(`Recibo impreso en ${result.printerName}`);
        onPrintSuccess?.();
        onCancel();
      } else {
        messageApi.warning({
          content: 'No se pudo imprimir, pero el proceso continuará',
          duration: 5,
        });
        // Continue the flow even if printing failed
        onPrintSuccess?.();
        onCancel();
      }
    } catch (error: any) {
      messageApi.warning({
        content: error?.data?.message || 'Error al imprimir, pero el proceso continuará',
        duration: 5,
      });
      console.error('Print error:', error);
      // Continue the flow even if printing failed
      onPrintSuccess?.();
      onCancel();
    }
  };

  return (
    <>
      {contextHolder}
      <Modal
        title="Vista Previa de Recibo"
        open={visible}
        onCancel={onCancel}
        width={800}
        zIndex={1001}
        footer={[
          <Button key="cancel" onClick={onCancel}>
            Cancelar
          </Button>,
          <Button
            key="preview"
            icon={<EyeOutlined />}
            onClick={handlePreview}
            loading={previewing}
          >
            Actualizar Vista Previa
          </Button>,
          <Button
            key="print"
            type="primary"
            icon={<PrinterOutlined />}
            onClick={handlePrint}
            loading={printing}
            disabled={!previewText}
          >
            Imprimir
          </Button>,
        ]}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
              Impresora:
            </label>
            <Select
              style={{ width: '100%' }}
              placeholder="Selecciona una impresora"
              value={selectedPrinter}
              onChange={setSelectedPrinter}
              loading={loadingPrinters}
              options={printers?.map((p) => ({
                label: `${p.name}${p.isDefault ? ' (Predeterminada)' : ''}`,
                value: p.id,
              }))}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
              Vista Previa:
            </label>
            <div
              style={{
                background: 'var(--bg-tertiary)',
                color: 'var(--text-primary)',
                padding: '16px',
                borderRadius: '4px',
                minHeight: '400px',
                maxHeight: '500px',
                overflow: 'auto',
                fontFamily: 'monospace',
                fontSize: '12px',
                whiteSpace: 'pre-wrap',
                border: '1px solid var(--border-color-strong)',
              }}
            >
              {previewing ? (
                <div style={{ textAlign: 'center', padding: '50px' }}>
                  <Spin tip="Generando vista previa..." />
                </div>
              ) : previewText ? (
                previewText
              ) : (
                <div style={{ textAlign: 'center', color: 'var(--color-neutral-text)', padding: '50px' }}>
                  Selecciona una impresora para ver la vista previa
                </div>
              )}
            </div>
          </div>
        </Space>
      </Modal>
    </>
  );
};

export default ReceiptPreviewModal;
